import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import type { ProjectFile } from '@bits2schema/shared';
import { api } from '../api/client';
import { useT } from '../i18n';
import { describeError } from '../utils/format';

interface ProjectRenameCardProps {
  project: ProjectFile;
  onChanged: () => void;
}

/**
 * E15-US1 — přejmenování projektu ze stejné obrazovky, kde se spravují
 * editoři (`ProjectPage.tsx` „Informace o DB" modal, umístěno přímo nad
 * `ProjectEditorsCard`). Creator i editor (obsahová úprava, ne správa
 * přístupu) — na rozdíl od `ProjectEditorsCard` tu není žádné omezení role.
 */
export function ProjectRenameCard({ project, onChanged }: ProjectRenameCardProps) {
  const t = useT();
  const [name, setName] = useState(project.name);
  useEffect(() => setName(project.name), [project.id, project.name]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t('schema.validation.emptyName'));
      setName(project.name);
      return;
    }
    if (trimmed === project.name) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await api.renameProject(project.id, trimmed);
      setSaved(true);
      onChanged();
    } catch (caught) {
      setError(describeError(caught, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="field">
        <label htmlFor="project-rename-name">{t('project.rename.nameLabel')}</label>
        <input
          id="project-rename-name"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            setSaved(false);
          }}
        />
      </div>
      {error && <div className="notice notice--error">{error}</div>}
      {saved && <div className="notice notice--success">{t('project.rename.saved')}</div>}
      <button
        type="button"
        style={{ marginTop: 'var(--space-3)' }}
        disabled={busy || name.trim() === project.name}
        onClick={() => void save()}
      >
        <Save size={16} />
        {t('project.rename.save')}
      </button>
    </div>
  );
}
